import { useEffect, useMemo, useState } from "react";
import { config } from "../environment";
import { getSessionPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";

type SessionUser = {
  id: string;
  email: string;
  tenantId: string;
} | null;

type AuthSession = {
  session: { id: string; userId: string };
  user: { id: string; email: string };
  // The stack's single tenants.id, carried alongside the session so clients can
  // stamp tenant_id on optimistic mutations before Zero has synced.
  tenantId: string;
  intercomUserJwt?: string;
  managedAiLifetimeCreditCents: number | null;
  monthlyTinesUnitsLimit?: number;
  managedAiProviderLocked?: boolean;
} | null;

// The server-signed Intercom messenger-security token, captured from the
// bootstrap fetch so the messenger doesn't need a second get-session call.
// Set before the signed-in tree (and thus the messenger) ever mounts.
let sessionIntercomUserJwt: string | undefined;

export function intercomUserJwt(): string | undefined {
  return sessionIntercomUserJwt;
}

let sessionMonthlyTinesUnitsLimit: number | undefined;
let sessionManagedAiLifetimeCreditCents: number | null = null;
let sessionManagedAiProviderLocked: boolean | undefined;

export function managedAiLifetimeCreditCents(): number | null {
  return sessionManagedAiLifetimeCreditCents;
}

export function monthlyTinesUnitsLimit(): number {
  return sessionMonthlyTinesUnitsLimit ?? config.monthlyTinesUnitsLimit;
}

export function managedAiProviderLocked(): boolean {
  return sessionManagedAiProviderLocked ?? config.managedAIProviderDisabled;
}

export function useSessionBootstrap() {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [user, setUser] = useState<SessionUser>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Retry with backoff rather than getting stuck on the loading screen
      // or treating a network blip as signed-out. The endpoint answers a
      // signed-out user with 200 and a null body, so any 5xx is a transient
      // infra failure (e.g. a gateway mid-deploy) that we retry, not sign-out.
      let delay = 500;
      while (!cancelled) {
        try {
          const response = await fetch(`${apiUrl()}${getSessionPath()}`, {
            credentials: "include",
          });
          if (response.status >= 500) throw new Error(String(response.status));
          const session = response.ok
            ? ((await response.json()) as AuthSession)
            : null;
          if (cancelled) return;
          sessionIntercomUserJwt = session?.intercomUserJwt;
          sessionManagedAiLifetimeCreditCents =
            session?.managedAiLifetimeCreditCents ?? null;
          sessionMonthlyTinesUnitsLimit = session?.monthlyTinesUnitsLimit;
          sessionManagedAiProviderLocked = session?.managedAiProviderLocked;
          if (session?.user && session.tenantId) {
            setUser({
              ...session.user,
              tenantId: session.tenantId,
            });
          } else {
            setUser(null);
          }
          setHasLoaded(true);
          return;
        } catch {
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay = Math.min(delay * 2, 10_000);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ user, hasLoaded }), [user, hasLoaded]);
}
